import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight, Users } from 'lucide-react';
import { useCommunitySessionsHook } from '@/hooks/useCommunitySessionsHook';
import { CommunitySessionCard } from './CommunitySessionCard';
import LoadingSpinner from '@/components/LoadingSpinner';

interface CommunitySessionsProps {
  limit?: number;
  featuredOnly?: boolean;
}

export const CommunitySessions: React.FC<CommunitySessionsProps> = ({
  limit = 6,
  featuredOnly = true
}) => {
  const { communitySessions, loading, error } = useCommunitySessionsHook(undefined, featuredOnly);

  // Don't render if there are no sessions
  if (loading && communitySessions.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || communitySessions.length === 0) {
    return null; // Don't show the section if there's an error or no sessions
  }

  // Limit sessions to display
  const sessionsToDisplay = communitySessions.slice(0, limit);

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-3xl font-bold bg-gradient-hero bg-clip-text text-transparent">
              Community Sessions
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Discover meditations shared by our community
            </p>
          </div>
        </div>

        <Button asChild variant="ghost" className="group">
          <Link to="/community" className="flex items-center gap-1">
            View All
            <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </div>

      {/* Sessions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sessionsToDisplay.map((session) => (
          <CommunitySessionCard
            key={session.id}
            session={session}
          />
        ))}
      </div>

      {/* Show View All link at bottom if there are more sessions */}
      {communitySessions.length > limit && (
        <div className="flex justify-center pt-4">
          <Button asChild variant="outline" size="lg">
            <Link to="/community" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Explore All {communitySessions.length} Community Sessions
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};
